import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Modal, Skeleton } from '@repo/design-system';
import { ink, space, typeRole } from '@repo/design-tokens';
import { getOfferedTwinTypes, getTwinById } from '@repo/front-end-services';
import { useUserStore } from '@repo/front-end-state';
import { countryCodeFor, countryName, resolveOnboardingRoute } from '@repo/front-end-utils';
import type { OnboardingRoute } from '@repo/front-end-utils';
import type {
  AdminTwin,
  OfferedTwinType,
  PositionSource,
  TimezoneSource,
} from '@repo/frontend-types';

import Config from '../../common/Config';
import { useAddressGeocode, type UseAddressGeocode } from './useAddressGeocode';
import { usePositionEditor } from './usePositionEditor';
import { useZoneForAddress } from './useZoneForAddress';
import type { TwinDetailsSave } from './useTwinDetailsSave';
import TwinDetailsWhere, { type AddressLine, type Identity } from './TwinDetailsWhere';
import TwinDetailsDerived from './TwinDetailsDerived';
import TwinDetailsSummary from './TwinDetailsSummary';
import SummaryIdentityCard, { type IdentityDraft } from './SummaryIdentityCard';
import SummaryAddressCard from './SummaryAddressCard';
import SummaryTimezoneCard from './SummaryTimezoneCard';
import SummaryPositionCard from './SummaryPositionCard';
import { nameForCreate } from './twinCreateForm';
import {
  applyTwinPatch,
  countryPatch,
  descriptionFieldText,
  descriptionPatch,
  formFieldsOf,
  formFromTwin,
  isDirty,
  isLocated,
  linePatch,
  looksLikePlaceholderOrigin,
  namePatch,
  summaryAddress,
  timezonePatch,
  TWIN_REQUEST_TIMEOUT_MS,
  type TwinDetailsForm,
  type TwinDetailsPatch,
} from './twinDetailsForm';

/**
 * **Twin details** — the wizard's first stage: where this twin is, and what time it keeps
 * (`twin-details-page-address`; frame 02, without address search, which stays parked).
 *
 * The country comes from a list and is stored as its two-letter code; the address lines are typed;
 * the timezone is worked out from the country, or from the position where a country has several,
 * and shown with where it came from; the position shows with its source. **Every change saves
 * straight to the twin** through the page's `useTwinDetailsSave`, and *Next: Opening Hours* moves
 * on without waiting.
 *
 * **Never the browser's zone** (R4.7): a twin with no timezone gets the one its address gives, or a
 * person chooses one. The step used to default to the operator's laptop clock, which is how a
 * Dubai site set up from London got London's day.
 *
 * **Two layouts** (`twin-details-page-summary`, R1). A new twin (the `create` route) gets the open
 * form above, *Where is it?* beside *Worked out from the address*. A twin that already exists gets
 * `TwinDetailsSummary`: four read-first cards, each with its own *Edit*, where the name can change
 * and a timezone change is confirmed first. Both render the same lifted pieces (`AddressFields`,
 * `ZonePicker`, `PositionControls`) and save through the same handlers below.
 *
 * **The map behind it** is the onboarding stage's washed map (`onboarding-persistent-scene-twin-
 * details-map`), not this step's: the page shows it and flies it. There is no *Find* here. Saving
 * the address looks it up at page level (`useAddressFlight`), which sets the position while the twin
 * has none; this step shows the lookup's outcome (`lookup`) and, for a twin with a plan but no
 * position, a message saying why the plan is not on the map (`unplacedNotice`).
 *
 * WHAT IT IS NOT. There is no orientation or altitude field. The summary is read-first: a twin is moved or turned on the Position on map step, which
 * shares this step's position handlers and save queue (`retire-edit-mode-position-write-path`);
 * altitude has no screen, since nothing reads it (epic retire-edit-mode decision).
 */

interface Props {
  className?: string;
  /** The twin being onboarded — `?twinBid=` from the wizard's entry point. */
  twinBid: string | undefined;
  accessToken: string | undefined;
  /** The page's route for this twin; `null` until the twin read settles (treated as not `create`). */
  route: OnboardingRoute | null;
  /** The page-level save queue (R6). */
  save: TwinDetailsSave;
  /** Opens Opening hours (R7). Never waits for a save. */
  onNext: () => void;
  /**
   * The summary's *Position and opening view* → the Position on map step
   * (`retire-edit-mode-position-write-path`, R9).
   */
  onOpenPositionOnMap?: () => void;
  /**
   * Reported upward so the nav tick, the Launch summary and Go Live's gate all agree. Called on
   * load and after every change.
   */
  onStateChange: (state: TwinDetailsState) => void;
  /**
   * The page's address lookup (`useAddressFlight`): its outcome shows in the position card. Absent,
   * the step keeps a lookup of its own that nothing starts, so the card stays quiet.
   */
  lookup?: UseAddressGeocode;
  /**
   * The plan-without-position message (`onboarding-persistent-scene-twin-details-map`, R2.3): the
   * page decides when it is open and holds the once-per-visit flag.
   */
  unplacedNotice?: { open: boolean; onClose: () => void };
  /** Where focus goes when the message closes and what opened it is gone: the step heading. */
  headingRef?: { readonly current: HTMLElement | null };
  /**
   * The page's record of this twin's position, moved by every save the queue accepts. A change in
   * it that this step did not make (the page's address lookup saving a match) is shown at once
   * (`onboarding-persistent-scene-twin-details-map`, R3.3, criterion 14).
   */
  recordPosition?: { latitude: string; longitude: string; source: PositionSource | null } | null;
}

export const UNPLACED_TITLE = 'Your twin isn’t on the map yet';
export const UNPLACED_BODY =
  'We’ll show your twin here once its location is set. Save its address, or place it on Position twin.';

export interface TwinDetailsState {
  /** Has a usable latitude/longitude — the SAME predicate Go Live gates on. */
  located: boolean;
  /**
   * The position is the legacy `51.4779 / 0` placeholder — a real coordinate, so it activates,
   * but almost certainly not where the twin is. See `looksLikePlaceholderOrigin`.
   */
  placeholder: boolean;
  /** A change not yet saved: typed and not left, held, in flight or failed (R1.3). */
  dirty: boolean;
  /** Single-line address for the Launch summary, or null. */
  address: string | null;
  timezone: string | null;
  /**
   * Where this answer came from (`twin-details-page-landing`, R3): `ready` once the step or the page
   * has read the twin, `failed` when the page's read found nothing (the Launch summary then says it
   * could not read the twin rather than "No position set"), `pending` before either has answered.
   * The step itself only ever reports `ready`.
   */
  read: 'pending' | 'ready' | 'failed';
}

type LoadState = 'loading' | 'ready' | 'failed';

/** What the twin holds, as far as this step knows: the record, then every change handed to save. */
interface Saved {
  form: TwinDetailsForm;
  positionSource: PositionSource | null;
  timezoneSource: TimezoneSource | null;
}

const EMPTY_SAVED: Saved = { form: formFromTwin(null), positionSource: null, timezoneSource: null };

/*
 * R2.3: a twin with a plan but no position is told why the plan is not on the map. The design
 * system's Modal: it traps focus, Escape and *Close* close it, and focus returns to what had it,
 * else the step heading. *OK* takes focus on open from this component's effect, which runs after
 * the Modal's own (a parent's effects follow its children's), so the Modal has already recorded the
 * element to return to. `autoFocus` would move focus first, and the Modal would record *OK* itself.
 */
const UnplacedNotice: React.FC<{
  open: boolean;
  onClose: () => void;
  headingRef?: { readonly current: HTMLElement | null };
}> = ({ open, onClose, headingRef }) => {
  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) okRef.current?.focus();
  }, [open]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={UNPLACED_TITLE}
      returnFocusTo={headingRef}
      actions={
        <Button ref={okRef} variant="primary" onClick={onClose}>
          OK
        </Button>
      }
    >
      {UNPLACED_BODY}
    </Modal>
  );
};

const OnboardingTwinDetails: React.FC<Props> = ({
  className,
  twinBid,
  accessToken,
  route,
  save,
  onNext,
  onOpenPositionOnMap,
  onStateChange,
  lookup,
  unplacedNotice,
  headingRef,
  recordPosition,
}) => {
  const { authMetadata } = useUserStore();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  /** The baseline for "changed": re-set from what was sent, trimmed, at every save (R6.3). */
  const [saved, setSaved] = useState<Saved>(EMPTY_SAVED);
  /** What is on screen: the typed lines and the typed coordinates. */
  const [form, setForm] = useState<TwinDetailsForm>(() => formFromTwin(null));
  /** Who the twin is — the folded identity line. */
  const [identity, setIdentity] = useState<Identity | null>(null);
  /**
   * The offered types, for the line's disc and type name. Read once per mount, and the only
   * reader on this page (traced: no other caller of `getOfferedTwinTypes` in the twin viewer's
   * wizard). `null` until read, or when the read fails — the line then falls back to `template`.
   */
  const [offered, setOffered] = useState<OfferedTwinType[] | null>(null);
  /**
   * The record as read: its lifecycle stage and organisation (the layout and the name check), the
   * description as read (`retire-edit-mode-twin-description`, R6), and the orientation and opening
   * view the Position card reads. The position itself is `saved`'s.
   */
  const [record, setRecord] = useState<AdminTwin | null>(null);
  /**
   * The Identity card's typed name and description; `null` while the card reads
   * (`twin-details-page-summary`, R3; `retire-edit-mode-twin-description`, R2).
   */
  const [draft, setDraft] = useState<IdentityDraft | null>(null);
  /** A rename handed to the queue and not refused, shown as the twin's name meanwhile. */
  const [queuedName, setQueuedName] = useState<string | null>(null);
  /**
   * A description handed to the queue, shown as the twin's description meanwhile. Boxed: a queued
   * clear (`{ value: null }`) is a value, not "nothing queued".
   */
  const [queuedDescription, setQueuedDescription] = useState<{ value: string | null } | null>(null);

  const ownLookup = useAddressGeocode(form, authMetadata?.tokens?.mapboxToken);
  const geocode = lookup ?? ownLookup;

  const countryCode = countryCodeFor(saved.form.country);
  const zone = useZoneForAddress({
    countryCode,
    latitude: saved.form.latitude,
    longitude: saved.form.longitude,
    storedZone: saved.form.iana_timezone,
    storedSource: saved.timezoneSource,
    route,
  });

  /**
   * Queue a change for THIS step's twin and move the screen and the baseline to what was sent. The
   * twin is named, not read at send time: Find answers after an await, and `?twinBid=` can change in
   * place meanwhile (the step is keyed by BID, so this instance's twin is the one it searched for).
   */
  const queue = save.save;
  const commit = useCallback(
    (patch: TwinDetailsPatch) => {
      queue(patch, twinBid);
      const fields = formFieldsOf(patch);
      setForm(prev => ({ ...prev, ...fields }));
      setSaved(prev => ({
        form: { ...prev.form, ...fields },
        positionSource: 'latitude' in patch ? (patch.position_source ?? null) : prev.positionSource,
        timezoneSource:
          'iana_timezone' in patch ? (patch.timezone_source ?? null) : prev.timezoneSource,
      }));
    },
    [queue, twinBid],
  );

  /** The pair's handlers, shared with Position on map (`retire-edit-mode-position-write-path`, R4). */
  const position = usePositionEditor({
    twinBid,
    saved: saved.form,
    address: form,
    geocode,
    commit,
  });
  const reseedPosition = position.reseed;

  /*
   * The newest read wins. `?twinBid=` can change in place, so a slow answer for the last twin could
   * otherwise land after this one — filling the form with the wrong twin's record and reporting it
   * up, where it would also stand in for the page's own read (`twin-details-page-landing`, swept in
   * at review: the same stale-answer problem the page guards against).
   */
  const loadGenRef = useRef(0);
  /**
   * The colleague's values, into the clashed fields only (R3.3): the form, the saved baseline and the
   * record take them; anything else on screen — other fields, a line being typed, an open Identity
   * card editing another field — is the person's and stays.
   */
  const showTheirs = useCallback(
    (record: AdminTwin, clashed: ReadonlySet<string>) => {
      const theirs = formFromTwin(record);
      const formKeys = (Object.keys(theirs) as Array<keyof typeof theirs>).filter(key =>
        clashed.has(key),
      );
      const pick = (prev: TwinDetailsForm): TwinDetailsForm => {
        const next = { ...prev };
        for (const key of formKeys) next[key] = theirs[key];
        return next;
      };
      const positionClashed = ['latitude', 'longitude', 'position_source'].some(k =>
        clashed.has(k),
      );
      const zoneClashed = ['iana_timezone', 'timezone_source'].some(k => clashed.has(k));
      setSaved(prev => ({
        form: pick(prev.form),
        positionSource: positionClashed ? (record.position_source ?? null) : prev.positionSource,
        timezoneSource: zoneClashed ? (record.timezone_source ?? null) : prev.timezoneSource,
      }));
      setForm(prev => pick(prev));
      setRecord(prev => {
        if (!prev) return record;
        const next = { ...prev } as Record<string, unknown>;
        clashed.forEach(key => {
          if (key in record) next[key] = (record as unknown as Record<string, unknown>)[key];
        });
        return next as unknown as AdminTwin;
      });
      if (clashed.has('name')) {
        setIdentity(prev => (prev ? { ...prev, name: record.name } : prev));
        setQueuedName(null);
      }
      if (clashed.has('description')) setQueuedDescription(null);
      // An open card editing a field that clashed holds the dropped value: it closes on theirs.
      if (clashed.has('name') || clashed.has('description')) setDraft(null);
      if (positionClashed) reseedPosition(theirs);
    },
    [reseedPosition],
  );
  // Read through a ref: the read runs once per twin, whatever the queue reports meanwhile.
  const unsavedPositionRef = useRef(save.unsavedPosition);
  unsavedPositionRef.current = save.unsavedPosition;
  const noteRecordRef = useRef(save.noteRecord);
  noteRecordRef.current = save.noteRecord;
  const readMarkRef = useRef(save.readMark);
  readMarkRef.current = save.readMark;
  const whenSettledRef = useRef(save.whenSettled);
  whenSettledRef.current = save.whenSettled;
  const writtenSinceRef = useRef(save.writtenSince);
  writtenSinceRef.current = save.writtenSince;
  /**
   * Read the twin. `clashed` makes it the re-read after a clash (`onboarding-multiplayer-twin-record-checks`,
   * R3.3): it waits for the rest of the refused save to land, then shows the colleague's value in
   * the clashed fields only — every other field, a line being typed and an open Identity card are
   * left as they are (review). A failed re-read leaves the step showing what it showed.
   */
  const load = useCallback(
    async (clashed?: ReadonlySet<string>) => {
      const quiet = clashed !== undefined;
      const gen = ++loadGenRef.current;
      if (!accessToken || !twinBid) return;
      if (!quiet) setLoadState('loading');
      if (quiet) await whenSettledRef.current?.();
      if (gen !== loadGenRef.current) return;
      const mark = readMarkRef.current?.();
      const read = await getTwinById(Config.middlewareApi, accessToken, twinBid, {
        timeout: TWIN_REQUEST_TIMEOUT_MS,
      });
      if (gen !== loadGenRef.current) return;
      // The stored values, before any pending overlay, are what the next save of each field says it
      // saw (`onboarding-multiplayer-twin-record-checks`, R3.1). After a clash, only the clashed
      // fields: the others keep what the screen shows, so a colleague's change to one of them that
      // nobody has seen is still refused (Copilot review).
      if (read) {
        const seen = clashed
          ? (Object.fromEntries(
              Object.entries(read).filter(([key]) => clashed.has(key)),
            ) as Partial<AdminTwin>)
          : read;
        noteRecordRef.current?.(twinBid, seen, mark, true);
      }
      // A position still being saved (Position on map moves the twin too; in flight, held or failed)
      // is shown rather than the record's, so it is never read back as its old value and Find never
      // saves over it (`retire-edit-mode-position-write-path`, review), as the name and description are.
      const pendingPosition = unsavedPositionRef.current;
      const record = read && pendingPosition ? applyTwinPatch(read, pendingPosition) : read;
      if (!record && quiet) return;
      if (record && clashed) {
        // A field saved again while this read was in flight keeps what was saved (Copilot review).
        const written = new Set(mark === undefined ? [] : writtenSinceRef.current(twinBid, mark));
        showTheirs(record, new Set(Array.from(clashed).filter(key => !written.has(key))));
        return;
      }
      if (!record) {
        // A failed read must NOT fall through to an empty form: a change saved over it would sit
        // beside blanks the step never read. `getTwinById` swallows its error and returns null, so
        // "no such twin" and "the read failed" are indistinguishable here — both mean don't guess.
        setLoadState('failed');
        return;
      }
      // Nothing is saved on load (R2.4): a stored *United Kingdom* stays until the country is next
      // chosen, and a twin with no timezone gets the derived one through the zone write below —
      // never the browser's.
      setSaved({
        form: formFromTwin(record),
        positionSource: record.position_source ?? null,
        timezoneSource: record.timezone_source ?? null,
      });
      setForm(formFromTwin(record));
      setRecord(record);
      setDraft(null);
      setQueuedName(null);
      setQueuedDescription(null);
      setIdentity({
        name: record.name,
        bid: record.bid,
        template: record.template,
        twin_template_id: record.twin_template_id,
      });
      reseedPosition(formFromTwin(record));
      setLoadState('ready');
    },
    [accessToken, twinBid, reseedPosition, showTheirs],
  );

  /*
   * A position saved for this twin while the step is on screen, by something other than its own
   * controls (the page's address lookup, `onboarding-persistent-scene-twin-details-map` R3.3), is
   * shown at once: the baseline, the fields and the source move to it. Only a CHANGE in the page's
   * record counts, never its first value (the step's own read is the baseline), and the step's own
   * saves already moved the baseline, so those are no change here.
   *
   * Two guards (Copilot review on #2505). A change is taken only once the step's own read is
   * ready, so a read that started before the save and lands after it cannot put the old position
   * back over it. And a pair being typed into *Enter coordinates* is kept: only the baseline moves,
   * as Position on map does, and the summary keeps its controls until the pair is saved or put back.
   */
  const recordKey = recordPosition
    ? `${recordPosition.latitude}|${recordPosition.longitude}|${recordPosition.source ?? ''}`
    : null;
  const seenRecordKeyRef = useRef(recordKey);
  const savedPairRef = useRef(saved.form);
  savedPairRef.current = saved.form;
  const typedPairRef = useRef(position.coords);
  typedPairRef.current = position.coords;
  useEffect(() => {
    if (loadState !== 'ready' || recordKey === seenRecordKeyRef.current) return;
    seenRecordKeyRef.current = recordKey;
    // A position still on its way to the server is newer than any the record holds.
    if (!recordPosition || unsavedPositionRef.current) return;
    const { latitude, longitude, source } = recordPosition;
    const now = savedPairRef.current;
    if (now.latitude.trim() === latitude.trim() && now.longitude.trim() === longitude.trim())
      return;
    const typed = typedPairRef.current;
    const typing =
      typed.latitude.trim() !== now.latitude.trim() ||
      typed.longitude.trim() !== now.longitude.trim();
    setSaved(prev => ({
      ...prev,
      form: { ...prev.form, latitude, longitude },
      positionSource: source,
    }));
    if (typing) return;
    setForm(prev => ({ ...prev, latitude, longitude }));
    reseedPosition({ latitude, longitude });
    // `recordPosition` is read through its key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordKey, loadState, reseedPosition]);

  useEffect(() => {
    void load();
  }, [load]);

  // A save of this twin was refused because someone else changed a field: show theirs (R3.3). Only a
  // clash that happens while the step is on screen: one standing from before it mounted is already in
  // the step's own first read, and a quiet first read that failed would never leave its skeleton.
  const clash = save.clash?.bid === twinBid ? save.clash : null;
  const clashSeq = clash?.seq;
  const clashFieldsRef = useRef(clash?.fields ?? []);
  clashFieldsRef.current = clash?.fields ?? [];
  const seenClashSeqRef = useRef(clashSeq);
  const loadRef = useRef(load);
  loadRef.current = load;
  const loadStateRef = useRef(loadState);
  loadStateRef.current = loadState;
  useEffect(() => {
    if (clashSeq === undefined || clashSeq === seenClashSeqRef.current) return;
    seenClashSeqRef.current = clashSeq;
    // A clash while the first read is still out: read in full, with its own failure screen, rather
    // than a quiet re-read that would leave the step on its skeleton (Copilot review).
    if (loadStateRef.current !== 'ready') {
      void loadRef.current();
      return;
    }
    // The clash names every field no read has brought back yet, an earlier clash's included.
    void loadRef.current(new Set(clashFieldsRef.current));
  }, [clashSeq]);

  // Bounded by the service; not retried — a failure only costs the disc and the type's name.
  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    void getOfferedTwinTypes(Config.middlewareApi, accessToken).then(types => {
      if (!cancelled) setOffered(types ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // R4.3: write the derived zone when the twin has none, or when it was worked out on the create
  // route. The baseline moves with the write, so the same answer is never written twice.
  const writeZone = zone.write?.zone;
  const writeSource = zone.write?.source;
  useEffect(() => {
    if (loadState !== 'ready' || !writeZone || !writeSource) return;
    commit(timezonePatch(writeZone, writeSource));
  }, [loadState, writeZone, writeSource, commit]);

  // Report state up so the nav tick and the Launch summary track the step — but only when the
  // reported VALUE changes.
  //
  // Two reasons, and the second is the sharper one. `OnboardingPage` is a large component that
  // reads the floorplan store and recomputes its per-floor collections on every render, so
  // handing it a fresh object on every keystroke re-rendered all of that for nothing. And an
  // effect that calls its callback unconditionally is one inline-lambda call site away from an
  // infinite loop: parent sets state → re-renders → new callback identity → effect re-runs →
  // parent sets state. Comparing the payload makes it idempotent whatever the caller passes.
  /** The name as far as this step knows: queued, else accepted this session, else as read. */
  // A rename still on its way (this visit's, or the queue's from before the step remounted) wins.
  const twinName = queuedName ?? save.unsaved?.name ?? save.savedName ?? identity?.name ?? '';
  /**
   * The description as far as this step knows (`retire-edit-mode-twin-description`, R6): queued in
   * this visit, else still pending in the page's queue (a save left in flight when the person moved
   * to another step), else accepted this session, else as read. A clear (`null`) at any level is a value. The record
   * is typed `string` but the column is nullable, so it is never trusted to be one.
   */
  const twinDescription: string | null = queuedDescription
    ? queuedDescription.value
    : save.unsaved && 'description' in save.unsaved
      ? (save.unsaved.description ?? null)
      : save.savedDescription
        ? save.savedDescription.value
        : (record?.description ?? null);
  const nameTyped = draft !== null && namePatch(draft.name, twinName) !== null;
  // R7: a typed, unsaved description counts as changed too.
  const descriptionTyped =
    draft !== null && descriptionPatch(draft.description, twinDescription) !== null;
  const dirty =
    save.pending ||
    isDirty({ ...form, ...position.coords }, saved.form) ||
    nameTyped ||
    descriptionTyped;
  const lastReportedRef = useRef<string>('');
  useEffect(() => {
    if (loadState !== 'ready') return;
    const next: TwinDetailsState = {
      located: isLocated(saved.form),
      placeholder: looksLikePlaceholderOrigin(saved.form),
      dirty,
      address: summaryAddress(saved.form),
      timezone: saved.form.iana_timezone.trim() || null,
      read: 'ready',
    };
    const key = JSON.stringify(next);
    if (key === lastReportedRef.current) return;
    lastReportedRef.current = key;
    onStateChange(next);
  }, [saved, dirty, loadState, onStateChange]);

  const onCountry = (code: string | null) => {
    // Choosing the country the twin already reads as sends nothing (criterion 7); nor does
    // clearing a field that was already empty.
    if (code === countryCode && (code !== null || saved.form.country.trim() === '')) return;
    setForm(prev => ({ ...prev, country: code ?? '' }));
    commit(countryPatch(code));
  };

  const onLineChange = (field: AddressLine, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  // Escape in a line: its saved value back (`twin-details-page-summary`, R2.4).
  const onLineRestore = (field: AddressLine) =>
    setForm(prev => ({ ...prev, [field]: saved.form[field] }));

  // R3.2: a line saves when it loses focus with a changed, trimmed value. Typing does not save.
  const onLineBlur = (field: AddressLine) => {
    const value = form[field].trim();
    if (value === saved.form[field].trim()) return;
    setForm(prev => ({ ...prev, [field]: value }));
    commit(linePatch(field, value));
  };

  const onZone = (chosen: string) => commit(timezonePatch(chosen, 'entered'));

  const onRename = (name: string) => {
    setQueuedName(name);
    commit({ name });
  };

  /** R3: a changed description, trimmed, or `null` for a blank one. An unchanged one sends nothing. */
  const onDescribe = (typed: string) => {
    const patch = descriptionPatch(typed, twinDescription);
    if (!patch) return;
    setQueuedDescription({ value: patch.description ?? null });
    commit(patch);
  };

  /*
   * R3.5: a refused rename puts the twin's name back and reopens the card on the refused name, with
   * the server's sentence. The refusal lives in the page's queue, so it is shown even when it came
   * back while this step was not on screen (Next never waits for a save) and the step has mounted
   * since — once its record is read, since the read starts the card closed. A refusal of a name no
   * longer queued (typed over since) says nothing.
   */
  const refusal = save.nameRefusal;
  // Read at the refusal, not a dependency: the description moving on must not re-run the reopen.
  const twinDescriptionRef = useRef(twinDescription);
  twinDescriptionRef.current = twinDescription;
  useEffect(() => {
    if (loadState !== 'ready' || !refusal || refusal.bid !== twinBid) return;
    if (queuedName !== null && queuedName !== refusal.name) return;
    setQueuedName(null);
    // R5: reopened with the description as it now stands; a card already open keeps its draft.
    setDraft(open => {
      if (open) return open;
      return { name: refusal.name, description: descriptionFieldText(twinDescriptionRef.current) };
    });
  }, [loadState, refusal, twinBid, queuedName]);
  /** The card closed on anything but the refused name: that refusal has been seen and left. */
  const onIdentityDraft = (next: IdentityDraft | null) => {
    if (next === null && refusal && twinBid && refusal.bid === twinBid) {
      save.dismissNameRefusal(twinBid);
    }
    setDraft(next);
  };
  const refusalShown =
    refusal &&
    refusal.bid === twinBid &&
    draft !== null &&
    nameForCreate(draft.name) === refusal.name
      ? refusal.message
      : null;

  if (!twinBid) {
    return (
      <div className={className}>
        <p className="lead">
          No twin is selected, so there is nothing to describe. Return to your twins and press the
          spanner on the twin you want to set up.
        </p>
      </div>
    );
  }

  /*
   * R1: which layout. The page's route wins; until it has one — or for good, when the page's own read
   * failed while this one succeeded — the step's record decides. Both decide `create` from the
   * lifecycle stage alone (`resolveOnboardingRoute`), so a live twin never gets the open form.
   */
  const layoutRoute: OnboardingRoute | null =
    route ??
    (record
      ? resolveOnboardingRoute({ lifecycleStage: record.lifecycle_stage, launchRecord: 'unknown' })
      : null);

  // A failed read with no route still shows *Try again*, never a skeleton for ever.
  if (loadState === 'loading' || (layoutRoute === null && loadState !== 'failed')) {
    return (
      <div className={className} aria-busy="true">
        <div className="skeletons">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} shape="line" />
          ))}
        </div>
      </div>
    );
  }

  if (loadState === 'failed') {
    return (
      <div className={className}>
        <p className="lead" role="alert">
          Could not read this twin&rsquo;s record, so the form was not filled in — saving now would
          overwrite the details it already has.
        </p>
        <div className="retry">
          <Button variant="frosted" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const unplacedModal = unplacedNotice ? (
    <UnplacedNotice
      open={unplacedNotice.open}
      onClose={unplacedNotice.onClose}
      headingRef={headingRef}
    />
  ) : null;

  if (layoutRoute !== 'create') {
    const positionControls = {
      saved: saved.form,
      coords: position.coords,
      errors: { latitude: position.errors.latitude, longitude: position.errors.longitude },
      onCoordChange: position.onCoordChange,
      onCoordCommit: position.onCoordCommit,
      onCoordRestore: position.onCoordRestore,
      geocode,
    };
    return (
      <>
        {unplacedModal}
        <TwinDetailsSummary
          className={className}
          identity={
            identity && (
              <SummaryIdentityCard
                identity={identity}
                offered={offered}
                name={twinName}
                description={twinDescription}
                organisationId={record?.organisation_id ?? null}
                getToken={async () => {
                  if (!accessToken) throw new Error('no token');
                  return accessToken;
                }}
                draft={draft}
                onDraft={onIdentityDraft}
                onRename={onRename}
                onDescribe={onDescribe}
                refusal={refusalShown}
              />
            )
          }
          address={
            <SummaryAddressCard
              saved={saved.form}
              form={form}
              onCountry={onCountry}
              onLineChange={onLineChange}
              onLineBlur={onLineBlur}
              onLineRestore={onLineRestore}
            />
          }
          timezone={
            <SummaryTimezoneCard
              storedZone={saved.form.iana_timezone}
              source={saved.timezoneSource}
              pointsElsewhere={zone.pointsElsewhere}
              countryCode={countryCode}
              countryLabel={countryName(countryCode)}
              onZone={onZone}
            />
          }
          position={
            record && (
              <SummaryPositionCard
                twin={{
                  ...record,
                  latitude: saved.form.latitude,
                  longitude: saved.form.longitude,
                  position_source: saved.positionSource,
                }}
                onOpenPositionOnMap={() => onOpenPositionOnMap?.()}
                {...positionControls}
              />
            )
          }
          onNext={onNext}
        />
      </>
    );
  }

  return (
    <>
      {unplacedModal}
      <div className={`${className ?? ''} twin-details-columns`}>
        <TwinDetailsWhere
          identity={identity}
          offered={offered}
          storedCountry={saved.form.country}
          form={form}
          onCountry={onCountry}
          onLineChange={onLineChange}
          onLineBlur={onLineBlur}
          onLineRestore={onLineRestore}
        />
        <TwinDetailsDerived
          storedZone={saved.form.iana_timezone}
          timezoneSource={saved.timezoneSource}
          answer={zone.answer}
          zonePending={zone.pending}
          pointsElsewhere={zone.pointsElsewhere}
          countryCode={countryCode}
          countryLabel={countryName(countryCode)}
          onZone={onZone}
          saved={saved.form}
          positionSource={saved.positionSource}
          coords={position.coords}
          coordErrors={{ latitude: position.errors.latitude, longitude: position.errors.longitude }}
          onCoordChange={position.onCoordChange}
          onCoordCommit={position.onCoordCommit}
          onCoordRestore={position.onCoordRestore}
          geocode={geocode}
          onNext={onNext}
        />
      </div>
    </>
  );
};

export default styled(OnboardingTwinDetails)`
  display: flex;
  flex-direction: column;
  gap: ${space[5]};

  /* R1.1: two columns from 1024 px, stacked below. Not \`.columns\`: the page's own layout rule of
     that name (\`OnboardingPage\`, display: flex) has the same specificity, comes later and wins. */
  &.twin-details-columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${space[4]};
    align-items: stretch;
  }

  @media (min-width: 1024px) {
    &.twin-details-columns {
      grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
    }
  }

  .lead {
    margin: 0;
    font: ${typeRole.body1.fontWeight} ${typeRole.body1.fontSize} / ${typeRole.body1.lineHeight}
      ${typeRole.body1.fontFamily};
    color: ${ink[2]};
  }

  .skeletons {
    display: flex;
    flex-direction: column;
    gap: ${space[4]};
    max-width: 480px;
  }

  .retry {
    width: 160px;
  }
`;
